import React, { Component } from "react";
import moment from "moment";

import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  ReferenceDot,
} from "recharts";

import {
  MetricTable,
  CustomizedXAxisTickTime,
  CustomizedYAxisTick,
  StandardTooltip,
  selectFormat,
  truncate,
  Error,
} from "./../../../Utils/Utils.js";

import {
  fetchInsights,
  RenderTooltipInsights,
  OpenSelectedInsight,
  CloseSelectedInsight,
  InsightsWithinTooltip,
  InsightsLineLabel,
  InsightsTickIndicator,
} from "./../../../Utils/InsightsUtils.js";

import { XRayTooltip } from "./../../../Utils/XRayTooltip/XRayTooltip.js";

import missing_image from "./../../../../i/missing_image.svg";

import { getXrayTooltipPosition } from "../../../../helpers/DOM";

const metricBar = (cell) => {
  const props = { ...cell };
  return <MetricTable {...props} graph={props.trends} graphType="bar" />;
};

const formatText = (tooltip_data) => {
  const previous_date = moment(tooltip_data.previous_date);
  const current_date = moment(tooltip_data.current_date);

  const diff = current_date.diff(previous_date, "days");

  return diff
    ? diff + " day" + (diff > 1 ? "s" : "") + " prior"
    : "No days prior";
};

class SharedSocialLineChart extends Component {
  constructor(props) {
    super(props);
    this.node = React.createRef();
    this.state = {
      widgetsData: props.data.data,
      widgetsInsights: [],
      width: 0,
      height: 0,
      tooltip: {
        show: false,
        x: 0,
        y: 0,
      },
    };

    this.divPosition = React.createRef();
    this.updatePosition = this.updatePosition.bind(this);
  }

  updatePosition() {
    this.closeTooltip();

    if (!this.chartRef) return;
    const { containerWidth, containerHeight } = this.chartRef.state;
    this.setState({ width: containerWidth, height: containerHeight });
  }

  UNSAFE_componentWillMount() {
    this.updatePosition();
  }

  componentDidMount() {
    // this.props.childRef(this);

    window.addEventListener("resize", this.updatePosition);
    window.addEventListener("click", this.handleClickOutside);

    setTimeout(() => {
      this.updatePosition();
    }, 500);

    // Fetch insights
    const { data, project_id, project, insights, insightsConfig } = this.props;
    if (insights) {
      const insightsPromise = fetchInsights(
        data,
        project,
        project_id,
        insightsConfig
      );
      if (insightsPromise !== undefined) {
        insightsPromise
          .then((data) => {
            if (data && data.length > 0) {
              this.setState({
                widgetsInsights: data,
              });
            }
          })
          .catch((error) => {
            console.log(error);
          });
      }
    }
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this.updatePosition);
    window.removeEventListener("click", this.handleClickOutside);
  }

  closeTooltip = () => {
    this.setState({
      tooltip: { show: false },
    });
  };

  handleClickOutside = (e) => {
    if (
      this.divPosition.current &&
      this.divPosition.current.contains(e.target)
    ) {
      return;
    } else {
      this.closeTooltip();
    }
  };

  getExportData = () => ({
    data: this.props.data.map((d) => {
      d["current_value_label"] = "This Day";
      d["previous_value_label"] = formatText(d);
      return d;
    }),
  });

  render() {
    const { data, title } = this.props;
    const { widgetsInsights } = this.state;

    const insightsData = widgetsInsights.length > 0;

    if (data.hasData === false) {
      return <Error minHeight={400} />;
    }

    return (
      <div ref={this.divPosition}>
        <div className="d-flex mt-2">
          <ResponsiveContainer height={200} width="100%" key={"graph"}>
            <ComposedChart
              data={this.props.preparedData || this.props.data}
              margin={{ top: 12, right: 0, bottom: 20, left: 0 }}
              onMouseOver={(e) => CloseSelectedInsight(this.node)}
              onMouseOut={(e) =>
                OpenSelectedInsight(
                  // this.state.insightsData,
                  this.node,
                  !this.state.tooltip.show
                )
              }
              onClick={(d) => {
                if (d !== null) {
                  const hasSharedLineChart = {
                    current_date: d.activePayload[0].payload.current_date,
                    current_value: d.activePayload[0].payload.current_value,
                    full_date: d.activePayload[0].payload.full_date,
                    today: d.activePayload[0].payload.today,
                    yesterday: d.activePayload[0].payload.yesterday,
                    previous_date: d.activePayload[0].payload.previous_date,
                    previous_value: d.activePayload[0].payload.previous_value,
                    week_name: d.activePayload[0].payload.week_name,
                    color: d.activePayload[0].color,
                  };
                  const indicator = "sharedSocialLineChart";

                  d.hasSharedLineChart = hasSharedLineChart;
                  d.indicator = indicator;
                  this.setState({
                    tooltip: { ...d, show: true },
                  });
                } else {
                  this.closeTooltip();
                }
              }}
            >
              <CartesianGrid
                stroke="#FEE5DE"
                strokeDasharray="3 3"
                vertical={false}
              />

              <Area
                type="monotone"
                dataKey="yesterday"
                fillOpacity={0.2}
                fill={this.props.color}
                stroke="none"
                isAnimationActive={false}
              >
                {this.state.widgetsInsights.length > 0 && (
                  <LabelList
                    dataKey="date"
                    content={
                      <InsightsLineLabel
                        height={250}
                        data={this.props.data}
                        insights={this.state.widgetsInsights}
                        showIndicator={!this.state.tooltip.show}
                        node={this.node}
                      />
                    }
                  />
                )}
              </Area>
              <Line
                type="monotone"
                dataKey="today"
                stroke={this.props.color}
                strokeWidth="2"
                dot={false}
                isAnimationActive={false}
              />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                scale="point"
                interval="preserveStartEnd"
                tick={<CustomizedXAxisTickTime />}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                mirror={true}
                tick={<CustomizedYAxisTick />}
              />

              {!this.state.tooltip.show && (
                <Tooltip
                  cursor={{ fill: "#eee", opacity: 0.5 }}
                  content={
                    <StandardTooltip
                      render={({ data }) => {
                        const payload = data(data.graph);
                        if (payload !== null) {
                          const tooltip_data = payload[0].payload;

                          const topContent = tooltip_data.topContent;
                          let IMAGE = {
                            backgroundImage: `url(${missing_image})`,
                          };

                          if (topContent) {
                            if (topContent.thumbnail)
                              IMAGE = {
                                backgroundImage: `url(${topContent.thumbnail})`,
                              };

                            topContent.value = topContent.views;
                            topContent.trends.forEach((chartData) => {
                              chartData.value = chartData.views;
                              chartData.label = chartData.date;
                              // eslint-disable-next-line no-self-assign
                              chartData.selected = chartData.selected;
                            });
                          }

                          return (
                            <div className="standard-tooltip">
                              {(tooltip_data.time || tooltip_data.name) && (
                                <header className="standard-tooltip__header">
                                  <span className="standard-tooltip__key-header">
                                    Date:
                                  </span>
                                  <span className="standard-tooltip__value-header">
                                    {selectFormat(
                                      "date",
                                      tooltip_data.name,
                                      "ddd, MMMM DD"
                                    )}
                                  </span>
                                </header>
                              )}
                              <main className="standard-tooltip__main">
                                {tooltip_data.today > 0 && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{ background: this.props.color }}
                                      />
                                      This day
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.today
                                      )}
                                    </span>
                                  </div>
                                )}

                                {tooltip_data.yesterday > 0 && (
                                  <div className="standard-tooltip__item">
                                    <span className="standard-tooltip__key">
                                      <span
                                        className="standard-tooltip__icon"
                                        style={{
                                          background: this.props.color,
                                          opacity: 0.2,
                                        }}
                                      />
                                      {formatText(tooltip_data)}
                                    </span>
                                    <span className="standard-tooltip__value">
                                      {selectFormat(
                                        "number",
                                        tooltip_data.yesterday
                                      )}
                                    </span>
                                  </div>
                                )}

                                {topContent && (
                                  <div className="standard-tooltip__dilimiter2 mt-3" />
                                )}
                                {topContent && (
                                  <div className="standard-tooltip__section-header my-2">
                                    Top content on this day.
                                  </div>
                                )}

                                {topContent ? (
                                  <div className="standard-tooltip__details--posts">
                                    <article className="content-table">
                                      <figure className="content-table__figure">
                                        <div
                                          className="content-table__img"
                                          style={IMAGE}
                                        />
                                      </figure>

                                      <section className="content-table__detail">
                                        <span className="standard-tooltip__value">
                                          {metricBar(topContent)}
                                        </span>
                                      </section>
                                    </article>

                                    <div className="standard-tooltip__text">
                                      {truncate(topContent.title, 200)}
                                    </div>
                                  </div>
                                ) : null}
                                {widgetsInsights.length > 0 && (
                                  <InsightsWithinTooltip
                                    data={widgetsInsights}
                                    tooltipDate={tooltip_data.date}
                                  />
                                )}
                              </main>
                            </div>
                          );
                        }
                      }}
                    />
                  }
                />
              )}
              {insightsData &&
                widgetsInsights.map((o, index) => {
                  return (
                    <ReferenceDot
                      x={o.date}
                      y={0}
                      key={title + index}
                      ifOverflow="extendDomain"
                      shape={
                        <InsightsTickIndicator
                          insights={widgetsInsights}
                          date={o.date}
                          node={this.node}
                        />
                      }
                    />
                  );
                })}
            </ComposedChart>
          </ResponsiveContainer>
          <div className="featured-insight-tooltip" ref={this.node}>
            {widgetsInsights.length > 0 && !this.state.tooltip.show && (
              <RenderTooltipInsights data={widgetsInsights} />
            )}
          </div>
        </div>
        {this.state.tooltip.activeCoordinate ? (
          <div
            className="xray-tooltip-container"
            style={{
              top: this.state.tooltip.activeCoordinate.y + 200 + "px",
              left:
                this.state.tooltip.activeCoordinate.x + 1062 + 220 <
                this.state.width
                  ? this.state.tooltip.activeCoordinate.x + 15 + "px"
                  : this.state.tooltip.activeCoordinate.x -
                    getXrayTooltipPosition(
                      this.divPosition.current,
                      this.state.tooltip.activeCoordinate
                    ) +
                    "px",
              opacity: this.state.tooltip.show ? 1.0 : 0.0,
              visibility: this.state.tooltip.show ? "visible" : "hidden",
            }}
          >
            <XRayTooltip
              data={this.state.tooltip}
              insights={widgetsInsights}
              close={this.closeTooltip.bind(this)}
            />
          </div>
        ) : null}
      </div>
    );
  }
}

export default SharedSocialLineChart;
